import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { LocationOrdersApiAdapter, LocationOrdersFilters } from '../adapters/LocationOrdersApiAdapter';
import { Service } from '../services/Service';

export const useLocationOrders = (filters: LocationOrdersFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new LocationOrdersApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['locationorders', filters],
    queryFn: ({ pageParam = 1 }) =>
      service.getPaginated({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos - datos frescos
    // 🎯 En React Query v5, usamos placeholderData para mantener los datos anteriores
    placeholderData: (previousData) => previousData,
  });

  const locationorders = data?.pages.flatMap((page) => page.locationorders) ?? [];

  // Obtener el total del API (siempre sera el mismo en todas las paginas)
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    locationorders,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    // Nuevas props para scroll infinito
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    // Stats útiles
    totalPages: data?.pages.length ?? 0,
    totalLocationOrders: locationorders.length,
    total: totalFromApi, // Total real del API (con filtros)
  };
};
